<template>
    <div class="spo-card rounded p-2" v-if="displayRetraitDossard">
        <div class="text-h6 text-darkblue font-weight-bold">Retrait de dossard</div>

        <div v-if="isInscriptionRelai" class="text-body-2 mb-3">
            Vous êtes inscrit à un relais.
            <span v-if="isCaptainEquipe">
                Vous êtes le capitaine de l'équipe. Veuillez nous indiquer le créneau de retrait de dossard souhaité.
            </span>
            <span v-else>
                Vous n'êtes pas le capitaine de l'équipe. Seul le capitaine peut choisir le créneau de retrait de
                dossard pour l'équipe.
            </span>

            <template v-if="!isCaptainEquipe">
                <div v-if="loadingChefEquipe" class="d-flex align-center ga-2 mt-2">
                    <v-progress-circular indeterminate color="primary" size="20" width="2"></v-progress-circular>
                    <span class="text-caption text-medium-emphasis">Chargement des informations de l'équipe…</span>
                </div>
                <v-alert v-else-if="chefEquipeError" type="warning" variant="tonal" class="mt-2" density="compact">
                    {{ chefEquipeError }}
                </v-alert>
                <div v-else-if="chefEquipeInfo" class="mt-2">
                    <div class="font-weight-medium">
                        Chef d'équipe : {{ chefEquipeInfo.prenom }} {{ chefEquipeInfo.nom }}
                    </div>
                    <div v-if="libelleCreneauChef" class="mt-1">
                        Créneau choisi pour l'équipe : {{ libelleCreneauChef }}
                    </div>
                    <div v-else class="mt-1 text-medium-emphasis">
                        Aucun créneau de retrait n'a encore été choisi par le capitaine.
                    </div>
                </div>
            </template>
        </div>
        <div v-else class="text-body-2 mb-3">Veuillez nous indiquer le créneau de retrait de dossard souhaité.</div>

        <div v-if="loading" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        </div>

        <template v-else-if="canSelectCreneau">
            <div v-if="hasCreneauSelectionne && !modeChangement">
                <v-alert type="success" variant="tonal" class="mb-3" density="comfortable">
                    <div class="font-weight-medium">Votre créneau</div>
                    <div class="mt-1">{{ libelleCreneauActuel }}</div>
                </v-alert>
                <SportipsButton secondary-action-button class="mt-1" @onClick="ouvrirChangementCreneau">
                    Changer de créneau
                </SportipsButton>
            </div>

            <div v-else>
                <label class="field_label">Créneau</label>
                <v-select v-model="selectionDraft" :items="selectItems" item-title="title" item-value="value"
                    density="compact" hide-details clearable :disabled="saving" :loading="saving"
                    placeholder="Choisissez un créneau" @update:model-value="onCreneauPicked"></v-select>

                <v-alert v-if="selectItems.length === 0" type="warning" variant="tonal" class="mt-3" density="compact">
                    Aucun créneau disponible pour le moment (tous complets ou fermés).
                </v-alert>

                <SportipsButton v-if="hasCreneauSelectionne && modeChangement" secondary-action-button class="mt-3"
                    @onClick="annulerChangement">
                    Annuler
                </SportipsButton>
            </div>
        </template>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import PublicService from "@/services/public.service";
import UserService from "@/services/user.service";

const CHAMP_KEY = "id_creneau_dossard";

export default {
    props: ["inscription"],
    data() {
        return {
            list_creneaux: [],
            loading: false,
            saving: false,
            modeChangement: false,
            selectionDraft: null,
            chefEquipeInfo: null,
            loadingChefEquipe: false,
            chefEquipeError: null,
        };
    },
    computed: {
        ...mapStores(useMainStore),
        displayRetraitDossard() {
            const current_datetime = new Date();
            const date_23_avril = new Date("2026-04-23T12:00:00");
            if (current_datetime < date_23_avril) {
                return false;
            }
            const id_course = this.inscription?.id_course;
            if (!id_course) {
                return false;
            }
            let list_ids_without_dossard = [1781, 1782, 1783, 1784, 0];
            return !list_ids_without_dossard.includes(Number(id_course));
        },
        canSelectCreneau() {
            if (this.loading) {
                return false;
            }
            if (this.isInscriptionRelai) {
                return this.isCaptainEquipe;
            }
            return true;
        },

        isInscriptionRelai() {
            if (!this.inscription?.id_course) {
                return false;
            }
            const list_ids_relais = [1345, 1419, 1420];
            return list_ids_relais.includes(Number(this.inscription.id_course));
        },
        isCaptainEquipe() {
            if (!this.inscription || !this.inscription.champs) {
                return false;
            }
            const champs = this.inscription.champs;
            if (!champs.is_captain_equipe) {
                return false;
            }
            return champs.is_captain_equipe == "1";
        },
        idCreneauActuel() {
            return this.readChampValue(CHAMP_KEY);
        },
        hasCreneauSelectionne() {
            const id = this.idCreneauActuel;
            return id !== null && id !== undefined && String(id).trim() !== "";
        },
        libelleCreneauActuel() {
            const id = this.idCreneauActuel;
            if (!id) {
                return "—";
            }
            const c = this.list_creneaux.find((x) => String(x.id) === String(id));
            return c ? this.formatCreneauLabel(c) : `Créneau #${id}`;
        },
        creneauxPourParcours() {
            const idCourse = this.inscription?.id_course;
            if (idCourse === undefined || idCourse === null || idCourse === "") {
                return this.list_creneaux;
            }
            const fid = String(idCourse);
            return this.list_creneaux.filter((item) => {
                const ids = String(item.id_course ?? "")
                    .split(";")
                    .map((s) => s.trim())
                    .filter(Boolean);
                if (ids.length === 0) {
                    return true;
                }
                return ids.includes(fid) || String(item.id_course) === fid;
            });
        },
        creneauxChoix() {
            const is_admin = this.mainStore.authAdmin.isLogged;
            if (is_admin) {
                return this.creneauxPourParcours.filter(
                    (c) => this.isCreneauOuvert(c)
                );
            }
            else {
                return this.creneauxPourParcours.filter(
                    (c) => this.isCreneauOuvert(c) && !this.isCreneauComplet(c)
                );
            }
        },
        selectItems() {
            return this.creneauxChoix.map((c) => ({
                title: this.formatCreneauLabel(c, true),
                value: c.id,
            }));
        },
        libelleCreneauChef() {
            if (!this.chefEquipeInfo) {
                return null;
            }
            if (this.chefEquipeInfo.libelle_creneau) {
                return String(this.chefEquipeInfo.libelle_creneau).trim() || null;
            }
            const id = this.chefEquipeInfo.id_creneau_dossard;
            if (id === null || id === undefined || String(id).trim() === "") {
                return null;
            }
            const c = this.list_creneaux.find((x) => String(x.id) === String(id));
            return c ? this.formatCreneauLabel(c) : `Créneau #${id}`;
        },
    },
    methods: {
        readChampValue(key) {
            const champs = this.inscription?.champs;
            if (!champs || champs[key] === undefined || champs[key] === null) {
                return null;
            }
            const v = champs[key];
            if (typeof v === "object" && v !== null && "value" in v) {
                const s = v.value;
                if (s === "" || s === undefined || s === null) {
                    return null;
                }
                return String(s);
            }
            return String(v);
        },
        writeChampValue(key, idStr) {
            let champs = this.inscription.champs;
            if (!champs) {
                champs = {};
                // Même objet partagé que le reste de la fiche coureur (champs formulaire)
                // eslint-disable-next-line vue/no-mutating-props
                this.inscription.champs = champs;
            }
            const existing = champs[key];
            if (existing && typeof existing === "object" && existing !== null && "value" in existing) {
                existing.value = idStr;
            } else {
                champs[key] = idStr;
            }
        },
        isCreneauOuvert(item) {
            return item.is_open == 1 || item.is_open === true || item.is_open === "1";
        },
        isCreneauComplet(item) {
            const cap = Number(item.capacity);
            const nb = Number(item.nb_inscrits ?? 0);
            if (!Number.isFinite(cap) || cap <= 0) {
                return false;
            }
            return nb >= cap;
        },
        formatCreneauLabel(c, withReste = false) {

            const nom = String(c.nom ?? "").trim() || `Créneau #${c.id}`;
            const cap = Number(c.capacity);
            if (Number.isFinite(cap) && cap > 0) {
                const reste = cap - Number(c.nb_inscrits ?? 0);
                const is_admin = this.mainStore.authAdmin.isLogged;
                if (is_admin) {
                    if (reste <= 0) {
                        return `${nom} (Complet - ${c.nb_inscrits}/${cap} places)`;
                    }
                    return `${nom} (${reste} place${reste > 1 ? "s" : ""} restante${reste > 1 ? "s" : ""})`;
                }
                if (withReste && reste <= 3) {
                    return `${nom} (${Math.max(0, reste)} place${reste > 1 ? "s" : ""} restante${reste > 1 ? "s" : ""})`;
                }
                return nom;
            }
            return nom;
        },
        ouvrirChangementCreneau() {
            this.modeChangement = true;
            this.selectionDraft = null;
        },
        annulerChangement() {
            this.modeChangement = false;
            this.selectionDraft = null;
        },
        async onCreneauPicked(id) {
            if (id === null || id === undefined || id === "") {
                return;
            }
            await this.persistCreneau(id);
        },
        creneauEncoreChoixValide(creneauIdStr) {
            const is_admin = this.mainStore.authAdmin.isLogged;
            if (is_admin) {
                return { ok: true };
            }
            const c = this.creneauxPourParcours.find((x) => String(x.id) === creneauIdStr);
            if (!c) {
                return { ok: false, message: "Ce créneau n'est plus disponible pour votre parcours." };
            }
            if (!this.isCreneauOuvert(c)) {
                return { ok: false, message: "Ce créneau n'est plus ouvert." };
            }
            if (this.isCreneauComplet(c)) {
                return {
                    ok: false,
                    message: "Ce créneau est complet. Veuillez en choisir un autre.",
                };
            }
            return { ok: true };
        },
        async persistCreneau(creneauId) {
            const idStr = String(creneauId);
            this.saving = true;
            try {
                const dejaSurCeCreneau = String(this.idCreneauActuel ?? "") === idStr;
                if (!dejaSurCeCreneau) {
                    const refreshed = await this.getListCreneaux();
                    if (!refreshed) {
                        this.mainStore.notification.send(
                            "Impossible de vérifier la disponibilité des créneaux. Réessayez.",
                            "error"
                        );
                        this.selectionDraft = null;
                        return false;
                    }
                    const check = this.creneauEncoreChoixValide(idStr);
                    if (!check.ok) {
                        this.mainStore.notification.send(check.message, "error");
                        this.selectionDraft = null;
                        return false;
                    }
                }
                const token = this.$route?.query?.token;
                if (token) {
                    if (!this.inscription.id) {
                        this.mainStore.notification.send(
                            "Impossible d'enregistrer le créneau (inscription inconnue).",
                            "error"
                        );
                        this.selectionDraft = null;
                        return false;
                    }
                    const res = await UserService.updateInscriptionChampByToken(
                        token,
                        this.inscription.id,
                        CHAMP_KEY,
                        idStr
                    );
                    if (res === false || res?.error) {
                        this.mainStore.notification.send(
                            typeof res?.error === "string"
                                ? res.error
                                : "Impossible d'enregistrer le créneau.",
                            "error"
                        );
                        this.selectionDraft = null;
                        return false;
                    }
                    this.writeChampValue(CHAMP_KEY, idStr);
                } else if ((this.mainStore.authUser?.isLogged || this.mainStore.authAdmin?.isLogged) && this.inscription.id) {
                    const res = await UserService.updateInscriptionChamp(
                        this.inscription.id,
                        CHAMP_KEY,
                        idStr
                    );
                    if (res === false || res?.error) {
                        this.mainStore.notification.send(
                            typeof res?.error === "string"
                                ? res.error
                                : "Impossible d'enregistrer le créneau.",
                            "error"
                        );
                        this.selectionDraft = null;
                        return false;
                    }
                    this.writeChampValue(CHAMP_KEY, idStr);
                } else {
                    this.mainStore.notification.send(
                        "Connexion requise pour enregistrer votre créneau.",
                        "error"
                    );
                    this.selectionDraft = null;
                    return false;
                }
                this.mainStore.notification.send("Créneau enregistré.", "success");
                this.modeChangement = false;
                this.selectionDraft = null;
                await this.getListCreneaux();
                return true;
            } finally {
                this.saving = false;
            }
        },
        normalizeListCreneauxResponse(response) {
            if (!response) {
                return [];
            }
            if (Array.isArray(response)) {
                return response;
            }
            if (Array.isArray(response.list_creneaux)) {
                return response.list_creneaux;
            }
            return [];
        },
        async getListCreneaux() {
            const id_module = this.inscription.id_module;
            const id_course = this.inscription.id_course;

            if (!id_module) {
                this.list_creneaux = [];
                return false;
            }
            const response = await PublicService.getListCreneaux(id_module, id_course);
            if (response === false) {
                this.list_creneaux = [];
                return false;
            }
            this.list_creneaux = this.normalizeListCreneauxResponse(response);
            return true;
        },
        async loadChefEquipe() {
            this.chefEquipeInfo = null;
            this.chefEquipeError = null;
            if (!this.isInscriptionRelai || this.isCaptainEquipe) {
                return;
            }
            const id_module = this.inscription?.id_module;
            if (!id_module) {
                return;
            }

            if (this.inscription?.id) {
                this.loadingChefEquipe = true;
                try {
                    const res = await PublicService.getChefEquipeRetraitDossard({
                        id_module,
                        id_inscription: this.inscription.id,
                    });
                    this.applyChefEquipeResponse(res);
                } finally {
                    this.loadingChefEquipe = false;
                }
                return;
            }
            this.chefEquipeError =
                "Connectez-vous ou utilisez le lien reçu par e-mail pour afficher le chef d'équipe et le créneau choisi.";
        },
        applyChefEquipeResponse(res) {
            if (res === false) {
                this.chefEquipeError = "Impossible de charger les informations du chef d'équipe. Réessayez plus tard.";
                return;
            }
            if (res && typeof res.error === "string" && res.error.trim() !== "") {
                this.chefEquipeError = res.error;
                return;
            }
            this.chefEquipeInfo = {
                nom: res?.nom ?? "",
                prenom: res?.prenom ?? "",
                id_creneau_dossard: res?.id_creneau_dossard ?? null,
                libelle_creneau: res?.libelle_creneau ?? null,
            };
        },
    },
    async mounted() {


        this.loading = true;
        try {
            await this.getListCreneaux();
        } finally {
            this.loading = false;
        }
        await this.loadChefEquipe();
    },
};
</script>

<style lang="scss" scoped></style>
